import { create } from 'zustand';

/**
 * Cross-cutting UI signals for table search flows (grids + search panels).
 * Grids call {@link reportSearchFailure} when a search POST fails; search panels
 * subscribe and reopen the filter section so users can adjust criteria.
 */
interface SearchUiStore {
  /** Increments on each failed grid search so panels can react without polling. */
  searchFailureGeneration: number;
  reportSearchFailure: () => void;
  /**
   * Set by search panels when the user clicks Search/Apply.
   * Consumed by the grid at the start of the next fetch (then cleared).
   */
  nextSearchUserInitiated: boolean;
  markNextSearchUserInitiated: () => void;
  consumeNextSearchUserInitiated: () => boolean;
}

export const useSearchUiStore = create<SearchUiStore>((set, get) => ({
  searchFailureGeneration: 0,
  reportSearchFailure: () =>
    set((state) => ({
      searchFailureGeneration: state.searchFailureGeneration + 1,
    })),
  nextSearchUserInitiated: false,
  markNextSearchUserInitiated: () => set({ nextSearchUserInitiated: true }),
  consumeNextSearchUserInitiated: () => {
    const userInitiated = get().nextSearchUserInitiated;
    if (userInitiated) {
      set({ nextSearchUserInitiated: false });
    }
    return userInitiated;
  },
}));

/** Call from search panel Apply/Search handlers before criteria hit the grid. */
export function markNextSearchUserInitiated(): void {
  useSearchUiStore.getState().markNextSearchUserInitiated();
}

/** Call at the start of a grid search fetch; returns whether the user explicitly triggered it. */
export function consumeNextSearchUserInitiated(): boolean {
  return useSearchUiStore.getState().consumeNextSearchUserInitiated();
}
